import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Breadcrumb, Spin, Table, Tooltip } from "antd";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { getRequest, postRequest } from "../../axios";
import ButtonField from "../../common/htmlComponents/ButtonField";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";
import { parseGeneralPreviewAndSendApprovalResponse } from "./generalProcurementPoUtils";

const GeneralProcurementPreviewAndSendApproval = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const pdfContainerRef = useRef(null);
  const buttonRef = useRef(null);

  const [comparativesRFQ, setComparativesRFQ] = useState([]);
  const [rfqNumber, setRfqNumber] = useState(null);
  const [poTotalAmount, setPoTotalAmount] = useState(0);
  const [apiLoading, setApiLoading] = useState(false);
  const [generalRfqId, setGeneralRfqId] = useState(null);
  const [btnLoading, setBtnLoading] = useState(false);
  const [buyer, setBuyer] = useState(null);
  const [firstApprover, setFirstApprover] = useState(null);
  const [isMaterialWise, setIsMaterialWise] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    const loadPreview = async () => {
      if (!id) return;
      setApiLoading(true);
      try {
        const response = await getRequest(
          `general-po-order/${id}/preview-and-send-for-approval/`,
        );

        if (!response?.data?.data) {
          ErrorNotificationMsg("Failed to load preview details");
          return;
        }

        const parsed = parseGeneralPreviewAndSendApprovalResponse(
          response.data,
        );

        setGeneralRfqId(parsed.generalRfq);
        setRfqNumber(parsed.rfqNumber);
        setBuyer(parsed.buyer ?? parsed.companyName);
        setFirstApprover(parsed.firstApprover);
        setIsMaterialWise(Boolean(parsed.isMaterialWise));
        setPoTotalAmount(parsed.poTotalAmount);
        setIsCompleted(parsed.poApproval);
        setComparativesRFQ(parsed.tableData ?? []);
      } catch (error) {
        console.error("Error loading general PO preview:", error);
        ErrorNotificationMsg("Failed to load preview details");
      } finally {
        setApiLoading(false);
      }
    };

    loadPreview();
  }, [id]);

  const downloadAsPDF = () => {
    if (!pdfContainerRef.current) {
      console.error("PDF container reference is not defined.");
      return;
    }

    setIsDownloading(true);
    if (buttonRef.current) {
      buttonRef.current.classList.add("hide-in-pdf");
    }

    html2canvas(pdfContainerRef.current, {
      useCORS: true,
      scale: 1.5,
      logging: true,
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("l", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const imgProps = pdf.getImageProperties(imgData);
        const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

        let heightLeft = imgHeight;
        let position = 0;
        const margin = 10;

        pdf.addImage(
          imgData,
          "PNG",
          0,
          position + margin,
          pdfWidth,
          imgHeight,
        );
        heightLeft -= pdfHeight - margin;

        while (heightLeft >= 0) {
          pdf.addPage();
          position = heightLeft - imgHeight;
          pdf.addImage(
            imgData,
            "PNG",
            0,
            position + margin,
            pdfWidth,
            imgHeight,
          );
          heightLeft -= pdfHeight - margin;
        }

        pdf.save(`Preview Approval Order-${rfqNumber}.pdf`);

        if (buttonRef.current) {
          buttonRef.current.classList.remove("hide-in-pdf");
        }
        setIsDownloading(false);
      })
      .catch(() => {
        if (buttonRef.current) {
          buttonRef.current.classList.remove("hide-in-pdf");
        }
        setIsDownloading(false);
      });
  };

  const data = useMemo(
    () =>
      comparativesRFQ?.flatMap((item, index) => {
        if (item.isMaterialWise) {
          return item.comparative.flatMap((comparative) =>
            comparative.vendorWise.flatMap((vendor) =>
              vendor.siteLocations.flatMap((location, locationIndex) => {
                const rows = [
                  {
                    key: `${index}-material-${locationIndex}`,
                    "Sr.No": locationIndex === 0 ? `${index + 1}` : null,
                    City: null,
                    "Site Name": null,
                    "Vendor Name":
                      locationIndex === 0 ? vendor.vendorName || "" : null,
                    rank: locationIndex === 0 ? vendor.rank : null,
                    Items: `${location.materialName || ""}`,
                    Quantity: `${location.quantity || ""}`,
                    UOM: `${location.uom || ""}`,
                    "Unit Rate": location.unitRateDisplay,
                    "Total Amount": location.totalAmountDisplay,
                    TotalAmountGST: location.totalAmountGstDisplay,
                  },
                ];

                if (locationIndex === vendor.siteLocations.length - 1) {
                  rows.push({
                    key: `${index}-grand-total`,
                    rowType: "grand-total",
                    "Vendor Name": <span className="subtotal1">Grand Total</span>,
                    "Total Amount": (
                      <div className="subtotal1">
                        ₹{" "}
                        {item.citySubTotalWithoutGST.toLocaleString("en-IN", {
                          maximumFractionDigits: 0,
                        })}
                      </div>
                    ),
                    TotalAmountGST: (
                      <div className="subtotal1">
                        ₹{" "}
                        {item.citySubTotal.toLocaleString("en-IN", {
                          maximumFractionDigits: 0,
                        })}
                      </div>
                    ),
                  });
                }

                return rows;
              }),
            ),
          );
        }

        const citySubTotal = item.citySubTotal || 0;

        return item.comparative.flatMap((comparative, compIndex) => {
          const siteSubTotal = comparative.siteSubTotal || 0;
          const siteSubTotalWithGst =
            comparative.siteSubTotalWithGST?.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            }) || 0;

          return comparative.vendorWise.flatMap((vendor, vendorIndex) =>
            vendor.siteLocations.flatMap((location, locationIndex) =>
              [
                {
                  key: `${index}-${compIndex}-${vendorIndex}-${locationIndex}`,
                  "Sr.No":
                    locationIndex === 0 && compIndex === 0
                      ? `${index + 1}`
                      : null,
                  City:
                    locationIndex === 0 && compIndex === 0
                      ? `${item.cityName}`
                      : null,
                  "Site Name":
                    locationIndex === 0 ? `${comparative.siteName}` : null,
                  "Vendor Name":
                    locationIndex === 0 ? vendor.vendorName || "" : null,
                  rank: locationIndex === 0 ? vendor.rank : null,
                  Items: `${location.materialName || ""}`,
                  Quantity: `${location.quantity || ""}`,
                  UOM: `${location.uom || ""}`,
                  "Unit Rate":
                    location.unitRateDisplay ??
                    `₹ ${
                      location.r_unit_price?.toLocaleString("en-IN", {
                        maximumFractionDigits: 0,
                      }) || 0
                    }`,
                  "Total Amount":
                    location.totalAmountDisplay ??
                    `₹ ${
                      location.r_total_price?.toLocaleString("en-IN", {
                        maximumFractionDigits: 0,
                      }) || 0
                    }`,
                  TotalAmountGST:
                    location.totalAmountGstDisplay ??
                    `₹ ${
                      location.total_amount_with_gst?.toLocaleString("en-IN", {
                        maximumFractionDigits: 0,
                      }) || 0
                    }`,
                },
                locationIndex === vendor.siteLocations.length - 1 && {
                  key: `${index}-${compIndex}-${vendorIndex}-SubTotal`,
                  "Site Name": <span className="subtotal1">Sub Total</span>,
                  "Total Amount": (
                    <div className="subtotal1">
                      ₹{" "}
                      {siteSubTotal.toLocaleString("en-IN", {
                        maximumFractionDigits: 0,
                      })}
                    </div>
                  ),
                  TotalAmountGST: (
                    <div className="subtotal1">
                      ₹{" "}
                      {siteSubTotalWithGst}
                    </div>
                  ),
                },
                locationIndex === vendor.siteLocations.length - 1 &&
                  compIndex === item.comparative.length - 1 && {
                    key: `${index}-CitySubTotal`,
                    "Site Name": <span className="citytotal2">City Total</span>,
                    "Total Amount": (
                      <div className="citytotal2">
                        ₹{" "}
                        {item.citySubTotalWithoutGST.toLocaleString("en-IN", {
                          maximumFractionDigits: 0,
                        })}
                      </div>
                    ),
                    TotalAmountGST: (
                      <div className="citytotal2">
                        ₹{" "}
                        {citySubTotal.toLocaleString("en-IN", {
                          maximumFractionDigits: 0,
                        })}
                      </div>
                    ),
                  },
              ].filter(Boolean),
            ),
          );
        });
      }),
    [comparativesRFQ],
  );

  const columns = [
    {
      title: "Sr.",
      dataIndex: "Sr.No",
      width: "30px",
    },
    ...(!isMaterialWise
      ? [
          {
            title: "City",
            dataIndex: "City",
            width: "40px",
          },
          {
            title: "Site",
            dataIndex: "Site Name",
            width: "30px",
          },
        ]
      : []),
    {
      title: "Vendor",
      dataIndex: "Vendor Name",
      width: 120,
    },
    {
      title: "Rank",
      dataIndex: "rank",
      width: 120,
      align: "center",
      render: (text) => {
        if (text?.comparison_ranking === "L1") {
          return (
            <>
              <Tooltip
                color="#094E8F"
                placement="left"
                title={<span style={{ color: "#fff" }}>Lowest Price</span>}
              >
                <p className="lowestven">{text?.comparison_ranking}</p>
              </Tooltip>
              <p style={{ color: "#094E8F", marginTop: "10px" }}>
                {text?.other_reason_text &&
                  text?.other_reason_text !== "N/A" &&
                  text?.selected_seasons}
              </p>
            </>
          );
        }

        if (text?.comparison_ranking === "L2") {
          return (
            <>
              <Tooltip
                color="#094E8F"
                placement="left"
                title={
                  <span style={{ color: "#fff" }}>Second Lowest Price</span>
                }
              >
                <p className="secondLowestven">{text?.comparison_ranking}</p>
              </Tooltip>
              <p style={{ color: "#094E8F", marginTop: "10px" }}>
                {text?.selected_seasons}
              </p>
              <p>
                {text?.other_reason_text &&
                  text?.other_reason_text !== "N/A" &&
                  text?.other_reason_text}
              </p>
            </>
          );
        }

        if (text?.comparison_ranking === "L3") {
          return (
            <>
              <Tooltip
                color="#094E8F"
                placement="left"
                title={
                  <span style={{ color: "#fff" }}>Third Lowest Price</span>
                }
              >
                <p className="thirdLowestven">{text?.comparison_ranking}</p>
              </Tooltip>
              <p style={{ color: "#094E8F", marginTop: "10px" }}>
                {text?.selected_seasons}
              </p>
              <p>{text?.other_reason_text && text?.other_reason_text}</p>
            </>
          );
        }

        return (
          <>
            <p>{text?.comparison_ranking}</p>
            <p>{text?.selected_seasons}</p>
            <p>{text?.other_reason_text && text?.other_reason_text}</p>
          </>
        );
      },
    },
    {
      title: "Items",
      dataIndex: "Items",
      width: "80px",
    },
    {
      title: "Qty.",
      dataIndex: "Quantity",
      width: "40px",
      align: "center",
    },
    {
      title: "UoM.",
      dataIndex: "UOM",
      width: "40px",
      align: "center",
      render: (text) => (text === "Cubic Meter" ? "Cu. Mtr." : text),
    },
    {
      title: "Unit Rate",
      dataIndex: "Unit Rate",
      width: "130px",
      align: "right",
    },
    {
      title: (
        <>
          <span>Total Amount</span>
          <br />
          <span>Without GST</span>
        </>
      ),
      dataIndex: "Total Amount",
      width: "100px",
      align: "right",
      render: (text, record) => (
        <div
          style={{
            marginRight:
              record.rowType === "grand-total" ||
              record["Site Name"] === "Sub Total" ||
              record["Site Name"]?.props?.children === "City Total"
                ? "0px"
                : "10px",
          }}
        >
          {record["Total Amount"]}
        </div>
      ),
    },
    {
      title: (
        <>
          <span>Total Amount</span>
          <br />
          <span>With GST</span>
        </>
      ),
      dataIndex: "TotalAmountGST",
      width: "100px",
      align: "right",
      render: (text, record) => (
        <div
          style={{
            marginRight:
              record.rowType === "grand-total" ||
              record["Site Name"] === "Sub Total" ||
              record["Site Name"]?.props?.children === "City Total"
                ? "0px"
                : "10px",
          }}
        >
          {record["TotalAmountGST"]}
        </div>
      ),
    },
  ];

  const handleSubmit = async () => {
    const rfqId = generalRfqId ?? id;
    if (!rfqId) {
      ErrorNotificationMsg("Invalid RFQ reference");
      return;
    }

    try {
      setBtnLoading(true);
      const response = await postRequest(
        `general-po-order/${rfqId}/send-purchase-order-for-approval/`,
      );

      if (response?.data?.success) {
        SuccessNotificationMsg(
          response?.data?.message || "PO order sent for approval successfully",
        );
        navigate("/general-procurement-list");
        return;
      }

      ErrorNotificationMsg(
        response?.data?.message || "Failed to send for approval",
      );
    } catch (error) {
      console.error("Failed to send general PO for approval:", error);
      ErrorNotificationMsg(
        error?.response?.data?.message || "Failed to send for approval",
      );
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>Preview and Send for Approval</h3>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/general-procurement-list">General RFQ History</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Preview and Send for Approval</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <div ref={buttonRef}>
            <ButtonField
              className="SaveGradiantBtn"
              type="primary"
              htmlType="PDF"
              label={isDownloading ? "Downloading..." : "Download PDF"}
              onClick={downloadAsPDF}
            />
          </div>
        </div>
      </div>

      <div className="stepscontentwrap">
        <div className="stepsContent">
          <section className="grid-sec mt-5" ref={pdfContainerRef}>
            <div className="PR-body" style={{ padding: "0" }}>
              <div className="rgq-num-wrap">
                <h5 className="rfq-number new">
                  RFQ Number : <span>{rfqNumber}</span>
                </h5>
              </div>
              <Spin spinning={apiLoading}>
                <Table
                  dataSource={data}
                  bordered
                  scroll={{ x: "auto" }}
                  size="middle"
                  pagination={false}
                  columns={columns}
                  className="comparativecustomtbl"
                />
              </Spin>

              <div className="TotalAMT">
                <label>Total Amount: </label>
                <div
                  className="citytotalNum"
                  style={{ width: "140px", textAlign: "end", fontWeight: "700" }}
                >
                  {poTotalAmount
                    ? `₹ ${poTotalAmount.toLocaleString("en-IN", {
                        maximumFractionDigits: 0,
                      })}`
                    : 0}
                </div>
              </div>
            </div>
          </section>
        </div>

        <div
          className="FormFooter bidftr"
          style={{
            padding: "16px",
            margin: "0",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h5 className="rfq-number new">
              Buyer :{" "}
              <span style={{ marginRight: "20px" }}>{buyer ? buyer : "N/A"}</span>
              Final Approver :{" "}
              <span style={{ marginRight: "20px" }}>
                {firstApprover ? firstApprover : "N/A"}
              </span>
            </h5>
          </div>
          <div className="FormFooter2">
            <ButtonField
              type="primary"
              className="CancelGradiantBtn"
              htmlType="button"
              label={t("Previous")}
              onClick={() =>
                navigate(`/general-procurement/summarypricecomparatives/${id}`)
              }
            />
            {isCompleted === false && (
              <ButtonField
                className="SaveGradiantBtn"
                type="primary"
                label="Send For Approval"
                onClick={handleSubmit}
                loading={btnLoading}
              />
            )}
            {isCompleted === true && (
              <ButtonField
                className="SaveGradiantBtn"
                type="primary"
                label="Back to General RFQ History"
                onClick={() => navigate("/general-procurement-list")}
                loading={btnLoading}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GeneralProcurementPreviewAndSendApproval;
